import React from 'react';
import { Minus, Plus } from 'lucide-react';

/**
 * QuantitySelector - Large touch-friendly quantity control
 * 
 * Features:
 * - Large +/- buttons (56px touch targets)
 * - Prevents going below min (default 1)
 * - Optional max limit
 * - Visual feedback on press
 */
export default function QuantitySelector({
  value = 1,
  onChange,
  min = 1,
  max = 99,
  size = 'large', // 'medium' | 'large'
  disabled = false,
  className = '',
}) {
  const handleDecrement = () => {
    if (value > min && !disabled) {
      onChange(value - 1);
    }
  };

  const handleIncrement = () => {
    if (value < max && !disabled) {
      onChange(value + 1);
    }
  };

  const buttonSize = size === 'large' ? 'w-14 h-14' : 'w-12 h-12';
  const fontSize = size === 'large' ? 'text-2xl' : 'text-xl';
  const iconSize = size === 'large' ? 24 : 20;

  return (
    <div 
      className={`flex items-center gap-4 ${className}`}
      role="group"
      aria-label="Quantity selector"
    >
      {/* Decrement Button */}
      <button
        type="button"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        className={`
          ${buttonSize}
          flex items-center justify-center
          rounded-full
          bg-[#252b33] border-2 border-white/10
          text-white
          transition-all duration-150
          ${value <= min || disabled
            ? 'opacity-40 cursor-not-allowed'
            : 'hover:bg-[#2d343d] hover:border-white/20 active:scale-95'
          }
        `}
        aria-label="Decrease quantity"
      >
        <Minus size={iconSize} />
      </button>

      {/* Quantity Display */}
      <span 
        className={`
          ${fontSize} font-bold text-white
          min-w-[3rem] text-center
          font-['Outfit']
        `}
        aria-live="polite"
        aria-atomic="true"
      >
        {value}
      </span>

      {/* Increment Button */}
      <button
        type="button"
        onClick={handleIncrement}
        disabled={disabled || value >= max}
        className={`
          ${buttonSize}
          flex items-center justify-center
          rounded-full
          bg-[#f5a623] border-2 border-[#f5a623]
          text-[#0f1419]
          transition-all duration-150
          ${value >= max || disabled
            ? 'opacity-40 cursor-not-allowed'
            : 'hover:brightness-110 active:scale-95'
          }
        `}
        aria-label="Increase quantity"
      >
        <Plus size={iconSize} />
      </button>
    </div>
  );
}
